import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  message,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Layout,
  Tag,
  Progress,
  Alert,
} from 'antd';
import {
  UserOutlined,
  CarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';
import { formatCurrency } from '../../utils/formatters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Content } = Layout;

const DriverReports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalDrivers: 0,
    activeDrivers: 0,
    totalJobs: 0,
    completedJobs: 0,
    averageRating: 0,
    totalHours: 0
  });
  const [drivers, setDrivers] = useState([]);

  const fetchDriverData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Use params for date range and a high limit to get all jobs for the period
      const params = {
        startDate: dateRange[0]?.toISOString(),
        endDate: dateRange[1]?.toISOString(),
        limit: 10000,
      };
      const res = await apiService.jobs.getAll(params);
      if (!res || !res.data || !Array.isArray(res.data.data)) {
        throw new Error('No data received from API');
      }
      let filteredJobs = res.data.data;
      // Group jobs by driver
      const driverStats = {};
      filteredJobs.forEach(job => {
        if (job.driver && job.driver._id) {
          if (!driverStats[job.driver._id]) {
            driverStats[job.driver._id] = {
              _id: job.driver._id,
              name: job.driver.name || 'Unknown Driver',
              totalJobs: 0,
              completedJobs: 0,
              pendingJobs: 0,
              totalRevenue: 0,
              van: job.van || null
            };
          }
          driverStats[job.driver._id].totalJobs++;
          if (job.jobStatus?.toLowerCase() === 'completed') {
            driverStats[job.driver._id].completedJobs++;
          } else if (job.jobStatus?.toLowerCase() === 'assigned' || job.jobStatus?.toLowerCase() === 'unassigned') {
            driverStats[job.driver._id].pendingJobs++;
          }
          if (job.payment?.totalAmount) {
            driverStats[job.driver._id].totalRevenue += job.payment.totalAmount;
          }
        }
      });
      const driverList = Object.values(driverStats);
      setDrivers(driverList);
      // Calculate overall stats
      const stats = {
        totalDrivers: driverList.length,
        activeDrivers: driverList.filter(d => d.totalJobs > 0).length,
        totalJobs: filteredJobs.length,
        completedJobs: filteredJobs.filter(j => j.jobStatus?.toLowerCase() === 'completed').length,
        averageRating: 4.5,
        totalHours: 0
      };
      setStats(stats);
    } catch (err) {
      setError(err.message || 'Failed to fetch driver data');
      message.error('Failed to fetch driver data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    console.log('Component mounted, fetching data...');
    fetchDriverData();
  }, [fetchDriverData]);

  const columns = [
    {
      title: 'Driver',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <span>{text || 'Unknown Driver'}</span>
        </Space>
      )
    },
    {
      title: 'Vehicle',
      dataIndex: 'van',
      key: 'van',
      render: (van) => {
        if (!van) return 'N/A';
        return (
          <Space>
            <CarOutlined />
            <span>{van.registration} ({van.make} {van.model})</span>
          </Space>
        );
      }
    },
    {
      title: 'Total Jobs',
      dataIndex: 'totalJobs',
      key: 'totalJobs',
      sorter: (a, b) => a.totalJobs - b.totalJobs
    },
    {
      title: 'Completed',
      dataIndex: 'completedJobs',
      key: 'completedJobs',
      render: (completed, record) => (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>{completed} / {record.totalJobs}</Text>
          <Progress 
            percent={Math.round((completed / record.totalJobs) * 100)} 
            size="small"
            status={completed === record.totalJobs ? 'success' : 'active'}
          />
        </Space>
      ),
      sorter: (a, b) => a.completedJobs - b.completedJobs
    },
    {
      title: 'Pending',
      dataIndex: 'pendingJobs',
      key: 'pendingJobs',
      render: (pending) => (
        <Tag color={pending > 0 ? 'warning' : 'success'}>
          {pending}
        </Tag>
      )
    },
    {
      title: 'Revenue',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => a.totalRevenue - b.totalRevenue
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>Loading driver data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={fetchDriverData}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>Driver Reports</Title>
            <Text type="secondary">View and analyze driver performance metrics</Text>
          </div>



          {/* Filters */}
          <Card>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Space>
                  <Text>Date Range:</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    allowClear={false}
                  />
                </Space>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Space>
                  <Text>Status:</Text>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    style={{ width: 200 }}
                  >
                    <Select.Option value="all">All Drivers</Select.Option>
                    <Select.Option value="active">Active</Select.Option>
                    <Select.Option value="inactive">Inactive</Select.Option>
                  </Select>
                </Space>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchDriverData}
                >
                  Apply Filters
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Stats Cards */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Total Drivers"
                  value={stats.totalDrivers}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Active Drivers"
                  value={stats.activeDrivers}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Card>
                <Statistic
                  title="Completed Jobs"
                  value={stats.completedJobs}
                  prefix={<ClockCircleOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Drivers Table */}
          <Card
            title="Driver Performance"
            extra={
              <Button type="primary" icon={<DownloadOutlined />}>
                Export to Excel
              </Button>
            }
          >
            {drivers.length === 0 ? (
              <Alert
                message="No Data"
                description="No driver data available for the selected filters."
                type="info"
                showIcon
              />
            ) : (
              <Table
                columns={columns}
                dataSource={drivers}
                rowKey="_id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} drivers`
                }}
              />
            )}
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default DriverReports; 